import { useCallback, useMemo } from 'react';
import { Field } from 'react-final-form';
import { Option } from 'react-rainbow-components';
import { query, where } from 'firebase/firestore';
import { PicklistValue } from '@rainbow-modules/listview/lib/components/AuditLogs/types';
import { hideAppSpinner, showAppSpinner } from '@rainbow-modules/app';
import { UniversalForm } from '@rainbow-modules/forms';
import Button from 'components/Button';
import { validateNotEmpty } from 'data/validators';
import usePhoneNumbers from 'data/firestore/phonenumber/useCollection';
import { formatIntlPhoneNumber } from 'data/services/phone/formatter';
import {
    Container, Content, Footer, FieldsSection, StyledInput, StyledPicklist,
} from './styled';

interface Props {
    agentId?: string;
    onRequestClose?: () => void;
    onSubmit?: (values: { username: string; friendlyName?: string }) => Promise<void>;
}

const FORM_ID = 'create-sip-user-form';

const CreateSipUserForm = ({
    agentId,
    onRequestClose = () => {},
    onSubmit,
}: Props) => {
    const { data: phoneNumbers, isLoading } = usePhoneNumbers({
        listQueryFn: (ref) => query(ref, where('agentId', '==', agentId)),
        disabled: !agentId,
        track: [agentId],
    });

    const phoneNumberOptions = useMemo(
        () => phoneNumbers.map((phoneNumber) => (
            <Option
                key={phoneNumber.id}
                name={phoneNumber.id}
                value={phoneNumber.number}
                label={formatIntlPhoneNumber(phoneNumber.number)}
            />
        )),
        [phoneNumbers],
    );

    const handleSubmit = useCallback(
        async (values: Record<string, unknown>) => {
            if (!onSubmit) return;
            showAppSpinner();
            try {
                await onSubmit(values as { username: string; friendlyName?: string });
            } finally {
                hideAppSpinner();
            }
        },
        [onSubmit],
    );

    return (
        <Container>
            <Content>
                <UniversalForm id={FORM_ID} onSubmit={handleSubmit}>
                    <FieldsSection>
                        <Field
                            name="username"
                            validate={validateNotEmpty('This field is required')}
                        >
                            {({ input, meta }) => (
                                <StyledPicklist
                                    label="Username"
                                    labelAlignment="left"
                                    required
                                    isLoading={isLoading}
                                    value={(() => {
                                        if (!input.value) return undefined;
                                        const found = phoneNumbers.find(
                                            (p) => p.number === input.value,
                                        );
                                        return found
                                            ? {
                                                name: found.id,
                                                value: found.number,
                                                label: formatIntlPhoneNumber(found.number),
                                            }
                                            : undefined;
                                    })()}
                                    onChange={({ value }: PicklistValue) => input.onChange(value)}
                                    error={meta.touched && meta.error ? meta.error : undefined}
                                >
                                    {phoneNumberOptions}
                                </StyledPicklist>
                            )}
                        </Field>
                        <Field
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            component={StyledInput}
                            name="friendlyName"
                            label="Friendly Name"
                            labelAlignment="left"
                            borderRadius="semi-square"
                            placeholder="e.g. Alice Smith"
                        />
                    </FieldsSection>
                </UniversalForm>
            </Content>
            <Footer>
                <Button
                    label="Cancel"
                    variant="outline-brand"
                    borderRadius="semi-square"
                    className="rainbow-m-right_medium"
                    onClick={onRequestClose}
                />
                <Button
                    label="Create"
                    variant="brand"
                    form={FORM_ID}
                    type="submit"
                    borderRadius="semi-square"
                />
            </Footer>
        </Container>
    );
};

export default CreateSipUserForm;
